import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ClipboardCopy, Upload, Wand2 } from "lucide-react";
import { rhm, apiError, money } from "../api";
import { Badge, Btn, Card, Field, Input, PageHead, Select, Textarea } from "../ui";
import { ListEditor, RowEditor } from "../editors";
import ProductLivePreview from "../components/ProductLivePreview";

const BASE = process.env.REACT_APP_BACKEND_URL;

const setIn = (obj, path, value) => {
    const [head, ...rest] = path.split(".");
    if (!rest.length) return { ...obj, [head]: value };
    return { ...obj, [head]: setIn(obj[head] || {}, rest.join("."), value) };
};

export default function RhmQuickProduct() {
    const nav = useNavigate();
    const qc = useQueryClient();
    const [name, setName] = useState("");
    const [text, setText] = useState("");
    const [imagesRaw, setImagesRaw] = useState("");
    const [status, setStatus] = useState("draft");
    const [mrp, setMrp] = useState("");
    const [selling, setSelling] = useState("");
    const [createScience, setCreateScience] = useState(true);
    const [showTemplate, setShowTemplate] = useState(false);
    const [parsed, setParsed] = useState(null);
    const [code, setCode] = useState("");

    const { data: meta } = useQuery({
        queryKey: ["quick-product-template"],
        queryFn: async () => (await rhm.get("/quick-product/template")).data,
    });

    useEffect(() => {
        if (meta?.next_code && !code) setCode(meta.next_code);
    }, [meta?.next_code, code]);

    const images = imagesRaw.split("\n").map((s) => s.trim()).filter(Boolean);

    const parse = useMutation({
        mutationFn: async () => (await rhm.post("/quick-product/parse", { text, name })).data,
        onSuccess: (d) => {
            setParsed(d);
            if (!name) setName(d.name);
            toast.success("Parsed — review everything below before saving");
            if (d.unknown_headings?.length) {
                toast.warning(`Not recognised: ${d.unknown_headings.join(", ")}`);
            }
        },
        onError: (e) => toast.error(apiError(e)),
    });

    const create = useMutation({
        mutationFn: async () => (await rhm.post("/quick-product/create", {
            name: name || parsed.name, product_code: code.trim().toUpperCase(), status, mrp: Number(mrp) || 0,
            selling_price: selling ? Number(selling) : null, images,
            product: parsed.product, website: parsed.website, formulation: parsed.formulation,
            science: parsed.science, create_science: createScience,
        })).data,
        onSuccess: ({ product }) => {
            qc.invalidateQueries({ queryKey: ["rhm-products"] });
            toast.success(`${product.name} created as ${product.product_code}`);
            nav(`/rhm/products/${product.id}`);
        },
        onError: (e) => toast.error(apiError(e)),
    });

    const upload = async (file) => {
        if (!file) return;
        const body = new FormData();
        body.append("file", file);
        try {
            const r = await fetch(`${BASE}/api/rhm/site/media?folder=Products`, {
                method: "POST", body,
                headers: { Authorization: `Bearer ${localStorage.getItem("rhm_token")}` },
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.detail || "Upload failed");
            setImagesRaw((prev) => [...prev.split("\n").filter(Boolean), d.url].join("\n"));
            toast.success("Image uploaded");
        } catch (e) {
            toast.error(e.message);
        }
    };

    const edit = (path) => (e) => setParsed((p) => setIn(p, path, e.target.value));
    const editValue = (path) => (v) => setParsed((p) => setIn(p, path, v));

    return (
        <div data-testid="rhm-quick-product">
            <PageHead
                title="Quick Add Product"
                sub="Paste one labelled block of text — ingredients, doses, combos, benefits, warnings and the science all land in the right fields."
                action={<Btn variant="ghost" onClick={() => nav("/rhm/products")} data-testid="qp-back">
                    <ArrowLeft className="w-3.5 h-3.5 inline mr-1.5" /> ALL PRODUCTS
                </Btn>}
            />

            <Card title="1 · Paste the details" action={
                <div className="flex items-center gap-2">
                    <Btn variant="ghost" data-testid="qp-toggle-template"
                        onClick={() => setShowTemplate(!showTemplate)}>
                        {showTemplate ? "HIDE TEMPLATE" : "SHOW TEMPLATE"}
                    </Btn>
                    <Btn variant="ghost" data-testid="qp-copy-template" onClick={() => {
                        navigator.clipboard?.writeText(meta?.template || "");
                        toast.success("Template copied — fill it in and paste it back");
                    }}><ClipboardCopy className="w-3.5 h-3.5 inline mr-1.5" /> COPY TEMPLATE</Btn>
                </div>}>
                {showTemplate && (
                    <pre data-testid="qp-template"
                        className="mb-5 max-h-72 overflow-auto border border-white/10 bg-black/40 p-4 text-[11px] leading-relaxed text-zinc-400 whitespace-pre-wrap">
                        {meta?.template || ""}
                    </pre>
                )}
                <div className="grid md:grid-cols-3 gap-4">
                    <Field label="Product name" hint="Or use a NAME: line in the text">
                        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="qp-name" />
                    </Field>
                    <Field label="Product code" hint={`Auto-suggested ${meta?.next_code || ""} — type your own if you prefer`}>
                        <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                            data-testid="qp-code" />
                    </Field>
                    <Field label="Status" hint="Set this manually after your review">
                        <Select value={status} onChange={(e) => setStatus(e.target.value)}
                            options={meta?.statuses || ["draft"]} data-testid="qp-status" />
                    </Field>
                    <Field label="MRP (₹)"><Input value={mrp} onChange={(e) => setMrp(e.target.value)} data-testid="qp-mrp" /></Field>
                    <Field label="Selling price (₹)" hint="Leave both empty for Coming Soon">
                        <Input value={selling} onChange={(e) => setSelling(e.target.value)} data-testid="qp-price" />
                    </Field>
                    <Field label="Also create the Science page">
                        <Select value={createScience ? "yes" : "no"} data-testid="qp-science-toggle"
                            onChange={(e) => setCreateScience(e.target.value === "yes")}
                            options={[{ value: "yes", label: "Yes — marked needs review" },
                                { value: "no", label: "No, product only" }]} />
                    </Field>
                </div>

                <div className="mt-5">
                    <Field label="Images" hint="One URL per line — or upload straight from your device">
                        <Textarea rows={3} value={imagesRaw} onChange={(e) => setImagesRaw(e.target.value)}
                            data-testid="qp-images" />
                    </Field>
                    <label className="mt-2 inline-flex items-center gap-2 mono text-[10px] tracking-[0.16em] text-gold cursor-pointer">
                        <Upload className="w-3.5 h-3.5" /> UPLOAD IMAGE
                        <input type="file" accept="image/*" className="hidden" data-testid="qp-upload"
                            onChange={(e) => upload(e.target.files?.[0])} />
                    </label>
                </div>

                <div className="mt-5">
                    <Field label="Product text">
                        <Textarea rows={14} value={text} onChange={(e) => setText(e.target.value)}
                            placeholder="Paste the filled-in template here…" data-testid="qp-text" />
                    </Field>
                </div>

                <div className="mt-5">
                    <Btn onClick={() => parse.mutate()} disabled={parse.isPending} data-testid="qp-parse">
                        <Wand2 className="w-3.5 h-3.5 inline mr-1.5" />
                        {parse.isPending ? "READING…" : "PARSE & REVIEW"}
                    </Btn>
                </div>
            </Card>

            {parsed && (
                <>
                    <Card title="2 · Review every field" className="mt-6" action={
                        <span data-testid="qp-parsed-count">
                            <Badge tone="info">
                                {parsed.formulation.ingredients.length} ingredient{parsed.formulation.ingredients.length === 1 ? "" : "s"} · {parsed.formulation.combos.length} combo{parsed.formulation.combos.length === 1 ? "" : "s"}
                            </Badge>
                        </span>}>
                        <div className="grid md:grid-cols-3 gap-4">
                            <Field label="Type"><Input value={parsed.product.product_type} onChange={edit("product.product_type")} data-testid="qp-r-type" /></Field>
                            <Field label="Pack size"><Input value={parsed.product.pack_size} onChange={edit("product.pack_size")} /></Field>
                            <Field label="Unit label"><Input value={parsed.product.unit_label} onChange={edit("product.unit_label")} /></Field>
                            <Field label="Tagline"><Input value={parsed.product.tagline} onChange={edit("product.tagline")} /></Field>
                            <Field label="Headline"><Input value={parsed.website.headline} onChange={edit("website.headline")} /></Field>
                            <Field label="Brand colour"><Input value={parsed.product.color} onChange={edit("product.color")} /></Field>
                            <Field label="Description" className="md:col-span-3">
                                <Textarea rows={3} value={parsed.product.description} onChange={edit("product.description")} data-testid="qp-r-description" />
                            </Field>
                        </div>

                        <div className="mt-6 grid md:grid-cols-2 gap-6">
                            <div>
                                <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">BENEFITS</div>
                                <ListEditor value={parsed.website.key_benefits} onChange={editValue("website.key_benefits")} testId="qp-r-benefits" />
                            </div>
                            <div>
                                <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">HIGHLIGHTS</div>
                                <ListEditor value={parsed.website.highlights} onChange={editValue("website.highlights")} />
                            </div>
                        </div>

                        <div className="mt-6">
                            <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">INGREDIENTS & DOSES</div>
                            <RowEditor testId="qp-r-ingredients" value={parsed.formulation.ingredients}
                                onChange={editValue("formulation.ingredients")}
                                fields={[{ key: "name", label: "Ingredient" }, { key: "quantity", label: "Qty" },
                                    { key: "unit", label: "Unit" }, { key: "botanical_name", label: "Botanical name" },
                                    { key: "combo_id", label: "Combo id" },
                                    { key: "description", label: "Description", type: "textarea", wide: true }]} />
                        </div>

                        {!!parsed.formulation.combos.length && (
                            <div className="mt-6">
                                <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">COMBOS</div>
                                <RowEditor testId="qp-r-combos" value={parsed.formulation.combos}
                                    onChange={editValue("formulation.combos")}
                                    fields={[{ key: "id", label: "Id" }, { key: "name", label: "Name" },
                                        { key: "color", label: "Colour" }, { key: "support_label", label: "Support label" },
                                        { key: "description", label: "Description", wide: true }]} />
                            </div>
                        )}

                        <div className="mt-6 grid md:grid-cols-2 gap-6">
                            <div>
                                <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">HOW TO USE</div>
                                <RowEditor testId="qp-r-steps" value={parsed.formulation.usage_steps}
                                    onChange={editValue("formulation.usage_steps")}
                                    fields={[{ key: "label", label: "No." }, { key: "title", label: "Step" },
                                        { key: "body", label: "Detail", wide: true }]} />
                            </div>
                            <div>
                                <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">WARNINGS</div>
                                <ListEditor value={parsed.formulation.warnings} onChange={editValue("formulation.warnings")} testId="qp-r-warnings" />
                            </div>
                        </div>

                        <div className="mt-6 grid md:grid-cols-2 gap-4">
                            <Field label="Directions"><Textarea rows={2} value={parsed.website.directions} onChange={edit("website.directions")} /></Field>
                            <Field label="Signature line"><Input value={parsed.website.signature_line} onChange={edit("website.signature_line")} /></Field>
                            <Field label="The Science (product page)" className="md:col-span-2">
                                <Textarea rows={3} value={parsed.website.science_text} onChange={edit("website.science_text")} />
                            </Field>
                            <Field label="Disclaimer" className="md:col-span-2">
                                <Textarea rows={2} value={parsed.formulation.disclaimer} onChange={edit("formulation.disclaimer")} />
                            </Field>
                        </div>

                        <div className="mt-6 grid md:grid-cols-2 gap-6">
                            <div>
                                <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">SPECIFICATIONS</div>
                                <RowEditor value={parsed.website.specifications} onChange={editValue("website.specifications")}
                                    fields={[{ key: "label", label: "Label" }, { key: "value", label: "Value" }]} />
                            </div>
                            <div>
                                <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">FAQS</div>
                                <RowEditor value={parsed.website.faqs} onChange={editValue("website.faqs")}
                                    fields={[{ key: "q", label: "Question", wide: true },
                                        { key: "a", label: "Answer", type: "textarea", wide: true }]} />
                            </div>
                        </div>

                        {createScience && (
                            <div className="mt-8 border-t border-white/10 pt-6">
                                <div className="mono text-[10px] tracking-[0.2em] text-gold mb-4">SCIENCE PAGE (SAVED AS “NEEDS REVIEW”)</div>
                                <div className="grid md:grid-cols-3 gap-4">
                                    <Field label="Category"><Input value={parsed.science.category} onChange={edit("science.category")} /></Field>
                                    <Field label="Traditional system">
                                        <Select value={parsed.science.traditional_system} onChange={edit("science.traditional_system")}
                                            options={["unani", "ayurveda", "nutrition", "combination"]} />
                                    </Field>
                                    <Field label="Positioning"><Input value={parsed.science.positioning} onChange={edit("science.positioning")} /></Field>
                                </div>
                                <div className="grid md:grid-cols-2 gap-4 mt-4">
                                    {[["overview", "Overview"], ["philosophy", "Formulation philosophy"],
                                        ["unani", "Unani perspective"], ["ayurveda", "Ayurvedic perspective"],
                                        ["nutrition", "Nutrition science"], ["why_selected", "Why these ingredients"],
                                        ["synergy", "How they work together"], ["cautions", "Cautions"]].map(([k, label]) => (
                                        <Field key={k} label={label}>
                                            <Textarea rows={3} value={parsed.science[k]} onChange={edit(`science.${k}`)} data-testid={`qp-r-sci-${k}`} />
                                        </Field>
                                    ))}
                                </div>
                            </div>
                        )}
                    </Card>

                    <Card title="3 · Live preview — the real product page" className="mt-6">
                        <ProductLivePreview height="70vh" product={{
                            ...parsed.product, name: name || parsed.name, product_code: code,
                            slug: "", images, status,
                            mrp: Number(mrp) || 0, price: Number(selling || mrp) || 0,
                            selling_price: selling ? Number(selling) : null,
                            coming_soon: status !== "active", purchasable: status === "active",
                            shop_type: "product", benefits: parsed.website.key_benefits,
                            website: parsed.website, formulation: parsed.formulation,
                        }} />
                    </Card>

                    <div className="mt-6 flex items-center gap-4">
                        <Btn onClick={() => create.mutate()} disabled={create.isPending} data-testid="qp-create">
                            {create.isPending ? "SAVING…" : `CREATE ${code || "PRODUCT"}`}
                        </Btn>
                        <span className="text-xs text-zinc-500">
                            Saves as <strong className="text-zinc-300">{status.replace("_", " ")}</strong>
                            {selling || mrp ? ` · ${money(Number(selling || mrp))}` : " · no price yet"}
                            {createScience ? " · science page created for review" : ""}
                        </span>
                    </div>
                </>
            )}
        </div>
    );
}
